import { CardBackgroundColorType, GradientStartPositionType, GradientType } from '~types'
import { hexToRgb } from './hexToRgb'
import {
  ColorPaletteBrandAccent800,
  ColorPaletteBrandSecondary600,
  ColorPaletteGray900,
  ColorPaletteSpecialtyGreen,
  ColorPaletteSpecialtyPink,
  ColorPaletteSpecialtyPurpleDark,
  ColorAliasNeutralBlack,
  ColorAliasNeutralWhite
} from '~styles/dist/tokens.mjs'

type LayoutDirection = 'horizontal' | 'vertical'

export type BackgroundGradientProps = {
  gradient?: GradientType
  color?: CardBackgroundColorType
  overrideColor?: CardBackgroundColorType | null
  direction: LayoutDirection
  isBelowBreakpoint?: boolean
  hasMediaInRightColumn?: boolean
  gradientStartPosition?: GradientStartPositionType
}

type BackgroundStyle = {
  background?: string
  backdropFilter?: string
}

const COLOR_MAP: Record<CardBackgroundColorType, string> = {
  tealDark: ColorPaletteBrandSecondary600,
  pinkLight: ColorPaletteSpecialtyPink,
  greenLight: ColorPaletteSpecialtyGreen,
  dark: ColorPaletteGray900,
  gold: ColorPaletteBrandAccent800,
  purple: ColorPaletteSpecialtyPurpleDark,
  transparent: 'transparent'
}

const whiteRgb = hexToRgb(ColorAliasNeutralWhite)
const blackRgb = hexToRgb(ColorAliasNeutralBlack)

const GRADIENT_BUILDERS = {
  radial: (position: string, direction: string, color: string) =>
    `radial-gradient(${position}, rgba(${whiteRgb}, 0.15) 0%, rgba(${whiteRgb}, 0) 100%),
     linear-gradient(${direction}, ${color} 0%, ${ColorAliasNeutralBlack} 90%)`,

  linear: (direction: string, color: string) =>
    `linear-gradient(${direction}, rgba(${blackRgb}, 0.15) 0%, ${color} 100%)`
}

const GRADIENT_PRESETS = {
  horizontal: {
    radial_gradient: {
      right: (color: string) => GRADIENT_BUILDERS.radial('15% 0% at 0% 100%', '90deg', color),
      left: (color: string) => GRADIENT_BUILDERS.radial('15% 0% at 100% 100%', '270deg', color)
    },
    linear_gradient: {
      right: (color: string) => GRADIENT_BUILDERS.linear('270deg', color),
      left: (color: string) => GRADIENT_BUILDERS.linear('90deg', color)
    },
    frosted_glass: () => `rgba(${blackRgb}, 0.1)`,
    transparent: () => 'transparent'
  },
  vertical: {
    radial_gradient: (color: string) => GRADIENT_BUILDERS.radial('79% 68% at 100% 100%', '0deg', color),
    linear_gradient: (color: string) => GRADIENT_BUILDERS.linear('174deg', color),
    frosted_glass: () => `rgba(${blackRgb}, 0.1)`,
    transparent: () => 'transparent'
  }
}

export const getBackgroundGradient = ({
  gradient,
  color,
  overrideColor,
  direction = 'horizontal',
  gradientStartPosition = 'left'
}: BackgroundGradientProps): BackgroundStyle | null => {
  if (!color) return { background: COLOR_MAP.transparent }

  const baseColor = overrideColor ? COLOR_MAP[overrideColor] : COLOR_MAP[color]

  // Transparent or empty gradient returns plain hex color
  if (!gradient || gradient === 'transparent') {
    return { background: baseColor }
  }

  // "Frosted glass" receives blur treatment
  if (gradient === 'frosted_glass') {
    return {
      background: `rgba(${blackRgb}, 0.1)`,
      backdropFilter: 'blur(4px)'
    }
  }

  const gradientConfig = GRADIENT_PRESETS[direction][gradient]

  // Horizontal (object with left/right/top/bottom positions)
  if (typeof gradientConfig === 'object') {
    const position = gradientStartPosition in gradientConfig ? gradientStartPosition : 'left'
    return {
      background: gradientConfig[position as keyof typeof gradientConfig](baseColor)
    }
  }

  // Vertical (function using color)
  return { background: (gradientConfig as (color: string) => string)(baseColor) }
}
